import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, ArrowRight, Check, Copy, Loader2, Search, Trash2, X } from 'lucide-react';
import { itemsService } from '../../../../../../services/itemsService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../../../services/stcBinGrpService';
import { stcMovementTypeService, StcMovementTypeDto } from '../../../../../../services/stcMovementTypeService';
import { stcSortingBoxDefService, StcSortingBoxDefDto } from '../../../../../../services/stcSortingBoxDefService';
import reportConfigurationService, { ReportConfiguration } from '../../../../../../services/reportConfigurationService';
import { itemBinSizeGroupService } from '../../../../../../services/itemBinSizeGroupService';
import { ItemBinSizeGroupDto } from '../../../../../../types/itemBinSizeGroup';
import { ItemsDto, ItemsPagedDto, BulkUpdateItemsRequest, BulkItemOperationResponse } from '../../../../../../types/items';
import { useTranslation } from '../../../../../../contexts/I18nProvider';

type TFunc = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

type Step = 'select-base' | 'select-fields' | 'preview' | 'running';
type TabType = 'basics' | 'warehouse' | 'classification' | 'location' | 'freetext';

interface ItemsCopyPropertiesModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedItemNos: string[];
    selectedItemData: ItemsPagedDto[];
    companyNo: string;
    onSuccess: () => void;
}

interface MasterData {
    binGroups: StcBinGrpDto[];
    reports: ReportConfiguration[];
    movementTypes: StcMovementTypeDto[];
    sortingBoxTypes: StcSortingBoxDefDto[];
    itemBinSizeGroups: ItemBinSizeGroupDto[];
}

const getPickRuleOptions = (t: TFunc): Record<number, string> => ({
    1: t('manager.modules.adminItemsLocations.itemsCopy.pickRule1', {}, 'FIFO'),
    2: t('manager.modules.adminItemsLocations.itemsCopy.pickRule2', {}, 'Antall'),
    3: t('manager.modules.adminItemsLocations.itemsCopy.pickRule3', {}, 'Prioritet'),
    4: t('manager.modules.adminItemsLocations.itemsCopy.pickRule4', {}, 'Batch fra ERP')
});

type FieldKey =
    | 'description1' | 'itemBarCode' | 'supplierItemNo'
    | 'binGrpNo' | 'pickRuleNo' | 'reportID'
    | 'movementTypeNo' | 'stcBoxTypeID' | 'itemBinSizeGroupID'
    | 'stcTransactionsYesNo' | 'putAwayToPickableBinYesNo' | 'newLineYesNo'
    | 'itemGrp1' | 'itemGrp2' | 'itemGrp3'
    | 'stdLocationName' | 'stdBinCode'
    | 'useSharedBinYesNo' | 'useMaxReplenishQtyYesNo' | 'maxReplenishQty'
    | 'freeTxt1' | 'freeTxt2' | 'freeTxt3' | 'freeTxt4' | 'freeTxt5'
    | 'freeTxt6' | 'freeTxt7' | 'freeTxt8' | 'freeTxt9' | 'freeTxt10';

interface FieldDef {
    key: FieldKey;
    tab: TabType;
    label: string;
    display: (item: ItemsDto, master: MasterData) => string;
}

const EMPTY = '<tom>';

const formatBool = (v: boolean | null | undefined): string => v === true ? 'Ja' : v === false ? 'Nei' : EMPTY;
const formatStr = (v: string | null | undefined): string => (v && v.length > 0) ? v : EMPTY;
const formatNum = (v: number | null | undefined): string => (v ?? null) !== null ? String(v) : EMPTY;

const getFieldDefs = (t: TFunc): FieldDef[] => {
    const pickRules = getPickRuleOptions(t);
    return [
    // Tab 1: Grunnleggende
    { key: 'description1', tab: 'basics', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldDescription', {}, 'Beskrivelse'), display: (i) => formatStr(i.description1) },
    { key: 'itemBarCode', tab: 'basics', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldGtin', {}, 'GTIN/Strekkode'), display: (i) => formatStr(i.itemBarCode) },
    { key: 'supplierItemNo', tab: 'basics', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldSupplierItemNo', {}, 'Leverandørvarenr'), display: (i) => formatStr(i.supplierItemNo) },
    { key: 'binGrpNo', tab: 'basics', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldBinGrp', {}, 'Lokasjonsgruppe'), display: (i, m) => {
        if (i.binGrpNo == null) return EMPTY;
        const g = m.binGroups.find(x => x.binGrpNo === i.binGrpNo);
        return g ? `${i.binGrpNo} - ${g.description1}` : String(i.binGrpNo);
    }},
    { key: 'pickRuleNo', tab: 'basics', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldPickRule', {}, 'Plukkregel'), display: (i) => {
        if (i.pickRuleNo == null) return EMPTY;
        return `${i.pickRuleNo} - ${pickRules[i.pickRuleNo] ?? '?'}`;
    }},
    { key: 'reportID', tab: 'basics', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldReport', {}, 'Rapport'), display: (i, m) => {
        if (i.reportID == null) return EMPTY;
        const r = m.reports.find(x => x.reportID === i.reportID);
        return r ? `${i.reportID} - ${r.reportName}` : String(i.reportID);
    }},
    // Tab 2: Lagerkonfigurasjon
    { key: 'movementTypeNo', tab: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldMovementType', {}, 'Bevegelseskode'), display: (i, m) => {
        if (i.movementTypeNo == null) return EMPTY;
        const mt = m.movementTypes.find(x => x.movementTypeNo === i.movementTypeNo);
        return mt ? `${i.movementTypeNo} - ${mt.description1}` : String(i.movementTypeNo);
    }},
    { key: 'stcBoxTypeID', tab: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldSortingBox', {}, 'Sorteringskasse'), display: (i, m) => {
        if (!i.stcBoxTypeID) return EMPTY;
        const b = m.sortingBoxTypes.find(x => x.stcBoxTypeID === i.stcBoxTypeID);
        return b ? `${i.stcBoxTypeID} - ${b.description1 || t('manager.modules.adminItemsLocations.itemsCopy.sortingBox', {}, 'Sorting Box')}` : i.stcBoxTypeID;
    }},
    { key: 'itemBinSizeGroupID', tab: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldSizeGroup', {}, 'Størrelsesgruppe'), display: (i, m) => {
        if (i.itemBinSizeGroupID == null) return EMPTY;
        const g = m.itemBinSizeGroups.find(x => x.itemBinSizeGroupID === i.itemBinSizeGroupID);
        return g ? `${i.itemBinSizeGroupID} - ${g.description1}` : String(i.itemBinSizeGroupID);
    }},
    { key: 'stcTransactionsYesNo', tab: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldStockControl', {}, 'Lagerstyring (StcTransactions)'), display: (i) => formatBool(i.stcTransactionsYesNo) },
    { key: 'putAwayToPickableBinYesNo', tab: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldReceiveToPick', {}, 'Mottak rett til plukk-lokasjon'), display: (i) => formatBool(i.putAwayToPickableBinYesNo) },
    { key: 'newLineYesNo', tab: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldNewLine', {}, 'Tilleggsvare'), display: (i) => formatBool(i.newLineYesNo) },
    // Tab 3: Klassifisering
    { key: 'itemGrp1', tab: 'classification', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldItemGrp1', {}, 'Varegruppe 1'), display: (i) => formatStr(i.itemGrp1) },
    { key: 'itemGrp2', tab: 'classification', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldItemGrp2', {}, 'Varegruppe 2'), display: (i) => formatStr(i.itemGrp2) },
    { key: 'itemGrp3', tab: 'classification', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldItemGrp3', {}, 'Varegruppe 3'), display: (i) => formatStr(i.itemGrp3) },
    // Tab 4: Lokasjon
    { key: 'stdLocationName', tab: 'location', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldStdLocation', {}, 'Standard lokasjon'), display: (i) => formatStr(i.stdLocationName) },
    { key: 'stdBinCode', tab: 'location', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldStdBinCode', {}, 'Fast plukklokasjon'), display: (i) => formatStr(i.stdBinCode) },
    { key: 'useSharedBinYesNo', tab: 'location', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldSharedBin', {}, 'Delt lokasjon'), display: (i) => formatBool(i.useSharedBinYesNo) },
    { key: 'useMaxReplenishQtyYesNo', tab: 'location', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldUseMaxReplenish', {}, 'Bruk maks påfyllmengde'), display: (i) => formatBool(i.useMaxReplenishQtyYesNo) },
    { key: 'maxReplenishQty', tab: 'location', label: t('manager.modules.adminItemsLocations.itemsCopy.fieldMaxReplenish', {}, 'Maks påfyllmengde'), display: (i) => formatNum(i.maxReplenishQty) },
    // Tab 5: Fritekst
    { key: 'freeTxt1', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 1 }, 'Fritekst 1'), display: (i) => formatStr(i.freeTxt1) },
    { key: 'freeTxt2', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 2 }, 'Fritekst 2'), display: (i) => formatStr(i.freeTxt2) },
    { key: 'freeTxt3', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 3 }, 'Fritekst 3'), display: (i) => formatStr(i.freeTxt3) },
    { key: 'freeTxt4', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 4 }, 'Fritekst 4'), display: (i) => formatStr(i.freeTxt4) },
    { key: 'freeTxt5', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 5 }, 'Fritekst 5'), display: (i) => formatStr(i.freeTxt5) },
    { key: 'freeTxt6', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 6 }, 'Fritekst 6'), display: (i) => formatStr(i.freeTxt6) },
    { key: 'freeTxt7', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 7 }, 'Fritekst 7'), display: (i) => formatStr(i.freeTxt7) },
    { key: 'freeTxt8', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 8 }, 'Fritekst 8'), display: (i) => formatStr(i.freeTxt8) },
    { key: 'freeTxt9', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 9 }, 'Fritekst 9'), display: (i) => formatStr(i.freeTxt9) },
    { key: 'freeTxt10', tab: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.freeText', { '0': 10 }, 'Fritekst 10'), display: (i) => formatStr(i.freeTxt10) }
    ];
};

const getTabs = (t: TFunc): { id: TabType; label: string }[] => [
    { id: 'basics', label: t('manager.modules.adminItemsLocations.itemsCopy.tabBasics', {}, 'Grunnleggende') },
    { id: 'warehouse', label: t('manager.modules.adminItemsLocations.itemsCopy.tabWarehouse', {}, 'Lagerkonfigurasjon') },
    { id: 'classification', label: t('manager.modules.adminItemsLocations.itemsCopy.tabClassification', {}, 'Klassifisering') },
    { id: 'location', label: t('manager.modules.adminItemsLocations.itemsCopy.tabLocation', {}, 'Lokasjon') },
    { id: 'freetext', label: t('manager.modules.adminItemsLocations.itemsCopy.tabFreeText', {}, 'Fritekst') }
];

export const ItemsCopyPropertiesModal: React.FC<ItemsCopyPropertiesModalProps> = ({
    isOpen,
    onClose,
    selectedItemNos,
    selectedItemData,
    companyNo,
    onSuccess
}) => {
    const { t } = useTranslation();
    const FIELD_DEFS = useMemo(() => getFieldDefs(t), [t]);
    const TABS = useMemo(() => getTabs(t), [t]);
    const [step, setStep] = useState<Step>('select-base');
    const [activeTab, setActiveTab] = useState<TabType>('basics');
    const [showChangesOnly, setShowChangesOnly] = useState(true);

    // Step 1: Base item search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<ItemsDto[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [baseItem, setBaseItem] = useState<ItemsDto | null>(null);

    // Step 2: Field selection
    const [selectedFields, setSelectedFields] = useState<Set<FieldKey>>(new Set());

    // Step 3: Targets (with possible exclusions)
    const [excludedItemNos, setExcludedItemNos] = useState<Set<string>>(new Set());

    // Step 4: Run
    const [isRunning, setIsRunning] = useState(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);

    // Master data
    const [master, setMaster] = useState<MasterData>({
        binGroups: [], reports: [], movementTypes: [], sortingBoxTypes: [], itemBinSizeGroups: []
    });
    const [isLoadingMaster, setIsLoadingMaster] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (!isOpen) return;
        setStep('select-base');
        setActiveTab('basics');
        setSearchTerm('');
        setSearchResults([]);
        setBaseItem(null);
        setSelectedFields(new Set());
        setExcludedItemNos(new Set());
        setIsRunning(false);
        setNotification(null);
    }, [isOpen, selectedItemNos]);

    // Load master data
    useEffect(() => {
        if (!isOpen || !companyNo) return;
        let cancelled = false;
        setIsLoadingMaster(true);
        Promise.all([
            stcBinGrpService.getCurrent().catch(() => []),
            reportConfigurationService.getAll(companyNo).catch(() => []),
            stcMovementTypeService.getCurrent().catch(() => []),
            stcSortingBoxDefService.getCurrent().catch(() => []),
            itemBinSizeGroupService.getCurrent().catch(() => [])
        ]).then(([binGroups, reports, movementTypes, sortingBoxTypes, itemBinSizeGroups]) => {
            if (cancelled) return;
            setMaster({ binGroups, reports, movementTypes, sortingBoxTypes, itemBinSizeGroups });
        }).finally(() => {
            if (!cancelled) setIsLoadingMaster(false);
        });
        return () => { cancelled = true; };
    }, [isOpen, companyNo]);

    // Debounced search
    useEffect(() => {
        if (!isOpen || step !== 'select-base') return;
        if (searchTerm.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        const handle = setTimeout(async () => {
            setIsSearching(true);
            try {
                const results = await itemsService.searchCurrentItems(searchTerm.trim());
                setSearchResults(results.slice(0, 50));
            } catch {
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
        return () => clearTimeout(handle);
    }, [searchTerm, isOpen, step]);

    const activeTargets = useMemo(
        () => selectedItemData.filter(i =>
            !excludedItemNos.has(i.itemNo) &&
            (!baseItem || i.itemNo !== baseItem.itemNo)
        ),
        [selectedItemData, excludedItemNos, baseItem]
    );

    const fieldDefsByTab = useMemo(() => {
        const map = new Map<TabType, FieldDef[]>();
        TABS.forEach(tab => map.set(tab.id, []));
        FIELD_DEFS.forEach(f => map.get(f.tab)!.push(f));
        return map;
    }, [TABS, FIELD_DEFS]);

    const toggleField = (key: FieldKey) => {
        setSelectedFields(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAllInTab = (tab: TabType, select: boolean) => {
        setSelectedFields(prev => {
            const next = new Set(prev);
            FIELD_DEFS.filter(f => f.tab === tab).forEach(f => {
                if (select) next.add(f.key);
                else next.delete(f.key);
            });
            return next;
        });
    };

    const selectAll = () => setSelectedFields(new Set(FIELD_DEFS.map(f => f.key)));
    const clearAll = () => setSelectedFields(new Set());

    const handleSelectBase = useCallback(async (item: ItemsDto) => {
        // Fetch full record to ensure we have every field populated
        try {
            const full = await itemsService.getItemByCompanyAndItemNo(companyNo, item.itemNo);
            setBaseItem(full);
        } catch {
            // Fallback to search result if individual fetch fails
            setBaseItem(item);
        }
    }, [companyNo]);

    const buildRequest = (): BulkUpdateItemsRequest => {
        if (!baseItem) throw new Error(t('manager.modules.adminItemsLocations.itemsCopy.noBaseItem', {}, 'Ingen basisvare valgt'));
        const req: BulkUpdateItemsRequest = {
            companyNo,
            itemNos: activeTargets.map(i => i.itemNo),
            sourceItemNo: baseItem.itemNo
        };
        selectedFields.forEach(key => {
            const value = (baseItem as unknown as Record<string, unknown>)[key];
            (req as unknown as Record<string, unknown>)[key] = value ?? null;
        });
        return req;
    };

    const executeRun = async () => {
        if (!baseItem || activeTargets.length === 0 || selectedFields.size === 0) return;
        setIsRunning(true);
        setStep('running');
        setNotification(null);
        try {
            const request = buildRequest();
            const response: BulkItemOperationResponse = await itemsService.bulkUpdateItems(request);
            const total = activeTargets.length;
            if (response.errors.length > 0) {
                setNotification({
                    type: 'warning',
                    message: t('manager.modules.adminItemsLocations.itemsCopy.copyPartial', { '0': response.successCount, '1': total, '2': response.errors.length }, `Kopierte til ${response.successCount} av ${total} varer. ${response.errors.length} feil oppstod.`)
                });
            } else {
                setNotification({
                    type: 'success',
                    message: t('manager.modules.adminItemsLocations.itemsCopy.copySuccess', { '0': response.successCount, '1': baseItem.itemNo }, `${response.successCount} vare(r) oppdatert fra basisvare ${baseItem.itemNo}.`)
                });
            }
            onSuccess();
        } catch (err) {
            setNotification({
                type: 'error',
                message: err instanceof Error ? err.message : t('manager.modules.adminItemsLocations.itemsCopy.copyFailed', {}, 'Kopiering feilet')
            });
            setStep('preview');
        } finally {
            setIsRunning(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <Copy className="w-6 h-6 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t('manager.modules.adminItemsLocations.itemsCopy.title', {}, 'Kopier egenskaper fra basisvare')}</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {t('manager.modules.adminItemsLocations.itemsCopy.targetsMarked', { '0': selectedItemNos.length }, `${selectedItemNos.length} mål-vare(r) markert`)}
                                {baseItem && <> · {t('manager.modules.adminItemsLocations.itemsCopy.baseItemLabel', {}, 'basisvare:')} <span className="font-medium">{baseItem.itemNo}</span></>}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" aria-label={t('common.buttons.close', {}, 'Lukk')}>
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Step indicator */}
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <ol className="flex items-center text-sm">
                        <StepBadge n={1} label={t('manager.modules.adminItemsLocations.itemsCopy.step1', {}, 'Velg basisvare')} active={step === 'select-base'} done={!!baseItem && step !== 'select-base'} />
                        <Chevron />
                        <StepBadge n={2} label={t('manager.modules.adminItemsLocations.itemsCopy.step2', {}, 'Velg felter')} active={step === 'select-fields'} done={selectedFields.size > 0 && step !== 'select-fields' && step !== 'select-base'} />
                        <Chevron />
                        <StepBadge n={3} label={t('manager.modules.adminItemsLocations.itemsCopy.step3', {}, 'Forhåndsvisning')} active={step === 'preview'} done={step === 'running'} />
                        <Chevron />
                        <StepBadge n={4} label={t('manager.modules.adminItemsLocations.itemsCopy.step4', {}, 'Kjør')} active={step === 'running'} done={false} />
                    </ol>
                </div>

                {/* Notification */}
                {notification && (
                    <div className={`mx-6 mt-4 rounded-lg p-3 flex items-start space-x-2 ${
                        notification.type === 'success' ? 'bg-green-50 border border-green-200' :
                        notification.type === 'error' ? 'bg-red-50 border border-red-200' :
                        'bg-yellow-50 border border-yellow-200'
                    }`}>
                        {notification.type === 'success' && <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'error' && <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
                        {notification.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />}
                        <p className={`text-sm flex-1 ${
                            notification.type === 'success' ? 'text-green-800' :
                            notification.type === 'error' ? 'text-red-800' :
                            'text-yellow-800'
                        }`}>
                            {notification.message}
                        </p>
                    </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {step === 'select-base' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">
                                {t('manager.modules.adminItemsLocations.itemsCopy.selectBaseIntro', {}, 'Søk og velg en korrekt konfigurert vare som skal brukes som mal. Verdier fra denne kopieres til de markerte mål-varene.')}
                            </p>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t('manager.modules.adminItemsLocations.itemsCopy.searchPlaceholder', {}, 'Søk på varenr, beskrivelse eller strekkode (min 2 tegn)')}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    autoFocus
                                />
                            </div>
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                {isSearching && (
                                    <div className="p-6 text-center text-gray-500 flex items-center justify-center space-x-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>{t('manager.modules.adminItemsLocations.itemsCopy.searching', {}, 'Søker...')}</span>
                                    </div>
                                )}
                                {!isSearching && searchTerm.trim().length < 2 && (
                                    <div className="p-6 text-center text-gray-400 text-sm">{t('manager.modules.adminItemsLocations.itemsCopy.typeMin2', {}, 'Skriv minst 2 tegn for å søke')}</div>
                                )}
                                {!isSearching && searchTerm.trim().length >= 2 && searchResults.length === 0 && (
                                    <div className="p-6 text-center text-gray-400 text-sm">{t('manager.modules.adminItemsLocations.itemsCopy.noHits', {}, 'Ingen treff')}</div>
                                )}
                                {!isSearching && searchResults.length > 0 && (
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="text-left p-2 font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsCopy.colItemNo', {}, 'Varenr')}</th>
                                                <th className="text-left p-2 font-medium text-gray-700">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                                <th className="text-left p-2 font-medium text-gray-700">{t('warehouse.general.barcode', {}, 'Strekkode')}</th>
                                                <th className="text-right p-2"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map(item => {
                                                const isTarget = selectedItemNos.includes(item.itemNo);
                                                return (
                                                    <tr key={item.itemNo} className={`border-b border-gray-100 hover:bg-blue-50 ${isTarget ? 'opacity-50' : ''}`}>
                                                        <td className="p-2 font-mono text-gray-900">{item.itemNo}</td>
                                                        <td className="p-2 text-gray-700">{item.description1}</td>
                                                        <td className="p-2 text-gray-500">{item.itemBarCode || '—'}</td>
                                                        <td className="p-2 text-right">
                                                            {isTarget ? (
                                                                <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">{t('manager.modules.adminItemsLocations.itemsCopy.alreadyTarget', {}, 'Allerede mål')}</span>
                                                            ) : (
                                                                <button
                                                                    onClick={() => handleSelectBase(item)}
                                                                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                                                >
                                                                    {t('manager.modules.adminItemsLocations.itemsCopy.selectAsBase', {}, 'Velg som basis')}
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            {baseItem && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-blue-900">{t('manager.modules.adminItemsLocations.itemsCopy.selectedBaseItem', {}, 'Valgt basisvare')}</p>
                                            <p className="text-lg font-mono text-blue-900 mt-1">{baseItem.itemNo}</p>
                                            <p className="text-sm text-blue-800">{baseItem.description1}</p>
                                            <p className="text-xs text-blue-700 mt-2">
                                                {t('manager.modules.adminItemsLocations.itemsCopy.baseSummary', { '0': baseItem.pickRuleNo ?? '—', '1': baseItem.binGrpNo ?? '—', '2': baseItem.movementTypeNo ?? '—' }, `Plukkregel: ${baseItem.pickRuleNo ?? '—'} · Lokasjonsgr: ${baseItem.binGrpNo ?? '—'} · Bevegelse: ${baseItem.movementTypeNo ?? '—'}`)}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setBaseItem(null)}
                                            className="text-blue-700 hover:text-blue-900 text-sm"
                                        >
                                            {t('manager.modules.adminItemsLocations.itemsCopy.switch', {}, 'Bytt')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 'select-fields' && baseItem && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    {t('manager.modules.adminItemsLocations.itemsCopy.selectFieldsIntroPrefix', {}, 'Velg hvilke felter som skal kopieres fra')} <span className="font-mono font-medium">{baseItem.itemNo}</span>.
                                </p>
                                <div className="flex items-center space-x-2">
                                    <button onClick={selectAll} className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">{t('manager.modules.adminItemsLocations.itemsCopy.selectAll', {}, 'Velg alle')}</button>
                                    <button onClick={clearAll} className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">{t('manager.modules.adminItemsLocations.itemsCopy.clearAll', {}, 'Fjern alle')}</button>
                                </div>
                            </div>

                            {/* Tab navigation */}
                            <div className="border-b border-gray-200">
                                <div className="flex space-x-6 overflow-x-auto">
                                    {TABS.map(tab => {
                                        const tabFields = fieldDefsByTab.get(tab.id) ?? [];
                                        const checkedCount = tabFields.filter(f => selectedFields.has(f.key)).length;
                                        return (
                                            <button
                                                key={tab.id}
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                                                    activeTab === tab.id
                                                        ? 'border-blue-600 text-blue-600'
                                                        : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                                                }`}
                                            >
                                                {tab.label}
                                                {checkedCount > 0 && (
                                                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                                        {checkedCount}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Field list for current tab */}
                            <div className="space-y-1">
                                <div className="flex items-center justify-end space-x-3 pb-2">
                                    <button
                                        onClick={() => toggleAllInTab(activeTab, true)}
                                        className="text-xs text-blue-600 hover:underline"
                                    >
                                        {t('manager.modules.adminItemsLocations.itemsCopy.selectAllInTab', {}, 'Velg alle i fanen')}
                                    </button>
                                    <button
                                        onClick={() => toggleAllInTab(activeTab, false)}
                                        className="text-xs text-blue-600 hover:underline"
                                    >
                                        {t('manager.modules.adminItemsLocations.itemsCopy.clearAllInTab', {}, 'Fjern alle i fanen')}
                                    </button>
                                </div>
                                {(fieldDefsByTab.get(activeTab) ?? []).map(field => {
                                    const checked = selectedFields.has(field.key);
                                    const displayValue = field.display(baseItem, master);
                                    return (
                                        <label
                                            key={field.key}
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                                checked ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleField(field.key)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className="text-sm font-medium text-gray-700">{field.label}</span>
                                            </div>
                                            <span className={`text-sm font-mono ml-3 truncate max-w-md text-right ${
                                                displayValue === EMPTY ? 'text-gray-400 italic' : 'text-gray-900'
                                            }`}>
                                                {displayValue}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {step === 'preview' && baseItem && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    {t('manager.modules.adminItemsLocations.itemsCopy.previewIntroPrefix', {}, 'Forhåndsvisning av endringer. Trykk')} <Trash2 className="inline w-3 h-3" /> {t('manager.modules.adminItemsLocations.itemsCopy.previewIntroSuffix', {}, 'for å utelukke en vare fra kjøringen.')}
                                </p>
                                <label className="flex items-center space-x-2 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={showChangesOnly}
                                        onChange={(e) => setShowChangesOnly(e.target.checked)}
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span>{t('manager.modules.adminItemsLocations.itemsCopy.showChangesOnly', {}, 'Vis kun felter med endringer')}</span>
                                </label>
                            </div>

                            <div className="border border-gray-200 rounded-lg overflow-auto max-h-[55vh]">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                        <tr>
                                            <th className="text-left p-2 font-medium text-gray-700 sticky left-0 bg-gray-50">{t('manager.modules.adminItemsLocations.itemsCopy.colItem', {}, 'Vare')}</th>
                                            <th className="text-left p-2 font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsCopy.colField', {}, 'Felt')}</th>
                                            <th className="text-left p-2 font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsCopy.colBefore', {}, 'Før')}</th>
                                            <th className="text-center p-2"></th>
                                            <th className="text-left p-2 font-medium text-gray-700">{t('manager.modules.adminItemsLocations.itemsCopy.colAfter', {}, 'Etter')}</th>
                                            <th className="text-right p-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeTargets.length === 0 && (
                                            <tr><td colSpan={6} className="p-6 text-center text-gray-400">{t('manager.modules.adminItemsLocations.itemsCopy.allExcluded', {}, 'Alle varer er utelukket. Legg til minst én vare for å fortsette.')}</td></tr>
                                        )}
                                        {activeTargets.flatMap(target => {
                                            const targetFields = FIELD_DEFS.filter(f => selectedFields.has(f.key));
                                            const rows = targetFields.map(field => {
                                                const before = field.display(target, master);
                                                const after = field.display(baseItem, master);
                                                const changed = before !== after;
                                                if (showChangesOnly && !changed) return null;
                                                return { field, before, after, changed };
                                            }).filter((r): r is { field: FieldDef; before: string; after: string; changed: boolean } => r !== null);

                                            if (rows.length === 0) {
                                                return [(
                                                    <tr key={target.itemNo} className="border-b border-gray-100 bg-gray-50">
                                                        <td className="p-2 font-mono text-gray-900 sticky left-0 bg-gray-50">{target.itemNo}</td>
                                                        <td colSpan={4} className="p-2 text-gray-500 italic">{t('manager.modules.adminItemsLocations.itemsCopy.noChangesForFields', {}, 'Ingen endringer for valgte felter')}</td>
                                                        <td className="p-2 text-right">
                                                            <button
                                                                onClick={() => setExcludedItemNos(prev => new Set(prev).add(target.itemNo))}
                                                                className="text-red-600 hover:text-red-800"
                                                                title={t('manager.modules.adminItemsLocations.itemsCopy.excludeItem', {}, 'Utelukk varen')}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )];
                                            }
                                            return rows.map((r, idx) => (
                                                <tr key={`${target.itemNo}-${r.field.key}`} className={`border-b border-gray-100 ${r.changed ? '' : 'opacity-60'}`}>
                                                    {idx === 0 ? (
                                                        <td className="p-2 font-mono text-gray-900 sticky left-0 bg-white border-r border-gray-100 align-top" rowSpan={rows.length}>
                                                            <div>{target.itemNo}</div>
                                                            <div className="text-xs text-gray-500 font-sans">{target.description1}</div>
                                                        </td>
                                                    ) : null}
                                                    <td className="p-2 text-gray-700">{r.field.label}</td>
                                                    <td className={`p-2 font-mono ${r.before === EMPTY ? 'text-gray-400 italic' : 'text-gray-700'}`}>{r.before}</td>
                                                    <td className="p-2 text-center"><ArrowRight className={`w-4 h-4 mx-auto ${r.changed ? 'text-green-600' : 'text-gray-300'}`} /></td>
                                                    <td className={`p-2 font-mono ${r.after === EMPTY ? 'text-gray-400 italic' : r.changed ? 'text-green-700 font-semibold' : 'text-gray-700'}`}>{r.after}</td>
                                                    {idx === 0 ? (
                                                        <td className="p-2 text-right align-top" rowSpan={rows.length}>
                                                            <button
                                                                onClick={() => setExcludedItemNos(prev => new Set(prev).add(target.itemNo))}
                                                                className="text-red-600 hover:text-red-800"
                                                                title={t('manager.modules.adminItemsLocations.itemsCopy.excludeItemFromRun', {}, 'Utelukk varen fra kjøringen')}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </td>
                                                    ) : null}
                                                </tr>
                                            ));
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {excludedItemNos.size > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start justify-between">
                                    <div className="flex items-start space-x-2">
                                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                        <p className="text-sm text-amber-800">
                                            {t('manager.modules.adminItemsLocations.itemsCopy.excludedSummary', { '0': excludedItemNos.size, '1': Array.from(excludedItemNos).join(', ') }, `${excludedItemNos.size} vare(r) utelukket fra kjøring: ${Array.from(excludedItemNos).join(', ')}`)}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setExcludedItemNos(new Set())}
                                        className="text-xs text-amber-700 hover:underline"
                                    >
                                        {t('manager.modules.adminItemsLocations.itemsCopy.reset', {}, 'Tilbakestill')}
                                    </button>
                                </div>
                            )}

                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <p className="text-sm text-blue-900">
                                    {t('manager.modules.adminItemsLocations.itemsCopy.summaryCopying', {}, 'Kopierer')} <span className="font-semibold">{selectedFields.size}</span> {t('manager.modules.adminItemsLocations.itemsCopy.summaryFieldsFromBase', {}, 'felt fra basisvare')}{' '}
                                    <span className="font-mono font-semibold">{baseItem.itemNo}</span> {t('manager.modules.adminItemsLocations.itemsCopy.summaryTo', {}, 'til')}{' '}
                                    <span className="font-semibold">{activeTargets.length}</span> {t('manager.modules.adminItemsLocations.itemsCopy.summaryItems', {}, 'vare(r).')}
                                </p>
                            </div>
                        </div>
                    )}

                    {step === 'running' && (
                        <div className="flex flex-col items-center justify-center py-16 space-y-4">
                            <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                            <p className="text-gray-700">{t('manager.modules.adminItemsLocations.itemsCopy.runningMessage', { '0': activeTargets.length }, `Kopierer egenskaper til ${activeTargets.length} vare(r)...`)}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-gray-50">
                    <div className="text-xs text-gray-500">
                        {isLoadingMaster && (
                            <span className="flex items-center space-x-1">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span>{t('manager.modules.adminItemsLocations.itemsCopy.loadingLookups', {}, 'Laster oppslagsdata...')}</span>
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                            disabled={isRunning}
                        >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                        </button>
                        {step === 'select-fields' && (
                            <button
                                onClick={() => setStep('select-base')}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                            >
                                {t('common.buttons.back', {}, 'Tilbake')}
                            </button>
                        )}
                        {step === 'preview' && (
                            <button
                                onClick={() => setStep('select-fields')}
                                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100"
                            >
                                {t('common.buttons.back', {}, 'Tilbake')}
                            </button>
                        )}
                        {step === 'select-base' && (
                            <button
                                onClick={() => setStep('select-fields')}
                                disabled={!baseItem}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {t('common.buttons.next', {}, 'Neste')}
                            </button>
                        )}
                        {step === 'select-fields' && (
                            <button
                                onClick={() => setStep('preview')}
                                disabled={selectedFields.size === 0}
                                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {t('manager.modules.adminItemsLocations.itemsCopy.nextWithCount', { '0': selectedFields.size }, `Neste (${selectedFields.size} felt)`)}
                            </button>
                        )}
                        {step === 'preview' && (
                            <button
                                onClick={executeRun}
                                disabled={activeTargets.length === 0 || selectedFields.size === 0 || isRunning}
                                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                                <Copy className="w-4 h-4" />
                                <span>{t('manager.modules.adminItemsLocations.itemsCopy.runCopy', { '0': activeTargets.length }, `Kjør kopiering (${activeTargets.length} varer)`)}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const StepBadge: React.FC<{ n: number; label: string; active: boolean; done: boolean }> = ({ n, label, active, done }) => (
    <li className="flex items-center space-x-2">
        <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
            active ? 'bg-blue-600 text-white' :
            done ? 'bg-green-500 text-white' :
            'bg-gray-200 text-gray-600'
        }`}>
            {done ? <Check className="w-3 h-3" /> : n}
        </span>
        <span className={`text-sm ${active ? 'text-blue-700 font-medium' : done ? 'text-green-700' : 'text-gray-500'}`}>
            {label}
        </span>
    </li>
);

const Chevron: React.FC = () => (
    <span className="mx-3 text-gray-300">›</span>
);
